'use client';

import { Service } from "../../lib/types";
import { useLayoutEffect, useEffect, useRef, useState } from "react";
import { usePathname } from 'next/navigation';
import Image from "next/image";
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactModal from '../ui/ContactModal';
import CTAButton from "../ui/CTAButton";
import Heading from "../ui/Heading";
import ShaderBackground from "../ui/ShaderBackground";
import AiShapeLg from "../assets/AiShapeLg";
import AiShapeSm from "../assets/AiShapeSm";
import type { ManualService } from "../../lib/data/services";
import { trackEvent } from '@/lib/analytics';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface ServicesSectionProps {
  servicesSection: {
    services: Service[] | ManualService[];
    title?: string;
    heading?: string;
  };
}


export default function Services({ servicesSection }: ServicesSectionProps) {
  const { services } = servicesSection;
  const pathname = usePathname();
  const sectionRef = useRef<HTMLElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAICardHovered, setIsAICardHovered] = useState(false);
  const [buttonPosition, setButtonPosition] = useState<{ x: number; y: number } | null>(null);
  const lgSvgRef = useRef<HTMLDivElement>(null);
  const smSvgRef = useRef<HTMLDivElement>(null);
  const mobileLgSvgRef = useRef<HTMLDivElement>(null);
  const mobileSmSvgRef = useRef<HTMLDivElement>(null);
  const desktopLgSvgRef = useRef<HTMLDivElement>(null);
  const desktopSmSvgRef = useRef<HTMLDivElement>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Animate SVG paths in headings using draw animation pattern (matching CareersLayout pattern)
  useLayoutEffect(() => {
    if (!cardsContainerRef.current) return;

    const ctx = gsap.context(() => {
      // Find all SVG elements within service cards
      const svgElements = cardsContainerRef.current?.querySelectorAll('.service-card-inner svg');
      
      if (!svgElements || svgElements.length === 0) return;

      svgElements.forEach((svg) => {
        const svgElement = svg as SVGSVGElement;
        const paths = svgElement.querySelectorAll('path');
        
        if (!paths.length) return;

        // Find the parent span/card to use as trigger (like CareersLayout does)
        const triggerElement = svgElement.closest('span') || svgElement.closest('.service-card-inner');
        if (!triggerElement) return;

        paths.forEach((path) => {
          const pathElement = path as SVGPathElement;
          const pathLength = pathElement.getTotalLength();
          
          // Check if path uses fill or stroke
          const hasFill = pathElement.getAttribute('fill') && pathElement.getAttribute('fill') !== 'none';
          const hasStroke = pathElement.getAttribute('stroke') && pathElement.getAttribute('stroke') !== 'none';

          if (hasStroke) {
            // Stroke-based draw animation (like CareersLayout underlines)
            gsap.set(pathElement, {
              strokeDasharray: pathLength,
              strokeDashoffset: pathLength,
            });

            gsap.to(pathElement, {
              strokeDashoffset: 0,
              duration: 1.2,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: triggerElement,
                start: 'top 80%',
                toggleActions: 'play none none none',
              },
            });
          } else if (hasFill) {
            // For filled paths, check if it's an underline (wide and short) or a circle/decorative element
            const viewBox = svgElement.getAttribute('viewBox');
            const width = svgElement.getAttribute('width');
            const height = svgElement.getAttribute('height');
            
            // Parse dimensions from viewBox or width/height attributes
            let svgWidth = 0;
            let svgHeight = 0;
            
            if (viewBox) {
              const parts = viewBox.split(' ');
              svgWidth = parseFloat(parts[2]) || 0;
              svgHeight = parseFloat(parts[3]) || 0;
            } else {
              svgWidth = parseFloat(width || '0');
              svgHeight = parseFloat(height || '0');
            }
            
            const isUnderline = svgWidth > svgHeight * 2; // Wide and short = underline
            
            if (isUnderline) {
              // Fill-based animation using scaleX (for filled underlines)
              gsap.set(svgElement, {
                scaleX: 0,
                transformOrigin: 'left center',
              });

              gsap.to(svgElement, {
                scaleX: 1,
                duration: 1.0,
                ease: 'power2.out',
                scrollTrigger: {
                  trigger: triggerElement,
                  start: 'top 80%',
                  toggleActions: 'play none none none',
                },
              });
            } else {
              // For circles and decorative filled paths, convert to stroke-based animation (like CareersLayout)
              // Set up stroke for drawing animation - stroke only, thick stroke
              gsap.set(pathElement, {
                strokeDasharray: pathLength,
                strokeDashoffset: pathLength,
                fill: '#E60000',
                stroke: '#E60000',
                strokeWidth: 2,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              });

              gsap.to(pathElement, {
                strokeDashoffset: 0,
                duration: 1.5,
                ease: 'power1.inOut',
                scrollTrigger: {
                  trigger: triggerElement,
                  start: 'top 80%',
                  toggleActions: 'play none none none',
                },
              });
            }
          }
        });
      });
    }, cardsContainerRef);

    return () => {
      try {
        ctx.revert();
      } catch (error) {
        console.warn('Services SVG animation cleanup error:', error);
      }
    };
  }, [services.length]);

  useLayoutEffect(() => {
    if (!sectionRef.current || !cardsContainerRef.current) return;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // DESKTOP: Horizontal scroll effect (>= 48.0625rem/769px)
      mm.add("(min-width: 769px)", () => {
        // Select only from desktop layout
        const desktopContainer = cardsContainerRef.current?.querySelector('.desktop-cards');
        if (!desktopContainer) return;
        
        const cards = gsap.utils.toArray<HTMLElement>(desktopContainer.querySelectorAll('.service-card-inner'));
        if (!cards.length) return;

        const slideDistance = 475;
        
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            pin: true,
            // pinReparent: true,
            anticipatePin: 1,
            start: 'top 40',
            end: '+=2200',
            pinSpacing: true,
            scrub: true,
            markers: false,
            id: 'services-timeline-desktop',
          }
        });

        // Stage 1: Card 2, Card 3, and Final card all slide left together
        tl.to([cards[1], cards[2], cards[cards.length - 1]].filter(Boolean), {
          x: -slideDistance,
          duration: 1,
          ease: 'none',
        }, 0);

        // Stage 2: Card 3 and Final card slide left together
        tl.to([cards[2], cards[cards.length - 1]].filter(Boolean), {
          x: -slideDistance * 2,
          duration: 1,
          ease: 'none',
        }, 1);

        // Stage 3: Final card slides left one more time
        if (cards[cards.length - 1]) {
          tl.to(cards[cards.length - 1], {
            x: -slideDistance * 3,
            duration: 1,
            ease: 'none',
          }, 2);
        }
      });

      // MOBILE: Card stacking effect (< 48rem/768px)
      mm.add("(max-width: 768px)", () => {
        // Select only from mobile layout
        const mobileContainer = cardsContainerRef.current?.querySelector('.mobile-cards');
        if (!mobileContainer) return;
        
        const cardWrappers = gsap.utils.toArray<HTMLElement>(mobileContainer.querySelectorAll('.card-wrapper'));
        const cards = gsap.utils.toArray<HTMLElement>(mobileContainer.querySelectorAll('.service-card-inner'));
        
        if (!cardWrappers.length || !cards.length) return;

        cardWrappers.forEach((wrapper, i) => {
          const card = cards[i];
          
          // Force hardware acceleration and stable positioning
          gsap.set(wrapper, { 
            transform: 'translateZ(0)',
            willChange: 'transform'
          });
          
          gsap.to(card, {
            transformOrigin: 'top center',
            ease: 'none',
            scrollTrigger: {
              trigger: wrapper,
              start: `top ${77 + 70 * i}`,
              end: 'bottom 750',
              endTrigger: mobileContainer,
              scrub: true,
              pin: wrapper,
              pinSpacing: false,
              anticipatePin: 0.5, // Pre-calculate pin position to prevent jump
              invalidateOnRefresh: true,
              markers: false,
              id: `card-${i + 1}`,
            }
          });
        });
      });
    }, sectionRef);

    return () => {
      try {
        ctx.revert();
      } catch (error) {
        console.warn('Services ScrollTrigger cleanup error:', error);
      }
    };
  }, [services.length]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleAICardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Offset button slightly from cursor for better UX
    setButtonPosition({ x: e.clientX, y: e.clientY - 40 });
  };

  const handleAICardMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    // Set position immediately on enter to prevent flash at (0,0)
    setButtonPosition({ x: e.clientX, y: e.clientY - 40 });
    setIsAICardHovered(true);
  };

  const handleAICardMouseLeave = () => {
    setIsAICardHovered(false);
    setButtonPosition(null);
  };

  // GSAP animation for large and small SVG in floating tooltip - animates once when tooltip appears
  useEffect(() => {
    if (!isAICardHovered || !lgSvgRef.current || !smSvgRef.current) return;

    // Set initial state - SVGs start at scale 1 (visible)
    gsap.set([lgSvgRef.current, smSvgRef.current], {
      scale: 1,
      rotation: 0
    });

    const animation = gsap.timeline({ delay: 0.6 });
    
    animation
      .to(smSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.1,
        ease: 'power2.in'
      })
      .to(lgSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.3,
        ease: 'power2.in'
      }, '<')
      .to(lgSvgRef.current, {
        scale: 1,
        rotation: 360,
        duration: 0.8,
        ease: 'back.out(1.2)'
      }, '>0.3')
      .to(smSvgRef.current, {
        scale: 1,
        duration: 0.4,
        rotation: '+=90',
        ease: 'back.out(1.7)'
      }, '<0.65');

    return () => {
      animation.kill();
    };
  }, [isAICardHovered]);

  // Detect touch device capability
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const checkTouchDevice = () => {
      // Check if device has touch capability
      const hasTouch = 'ontouchstart' in window || 
                       navigator.maxTouchPoints > 0 || 
                       (navigator as any).msMaxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
    };
    
    checkTouchDevice();
  }, []);

  // GSAP animation for mobile button SVG - runs every 6.5 seconds (same as AiStickyButton)
  useEffect(() => {
    if (!mobileLgSvgRef.current || !mobileSmSvgRef.current) return;

    const animation = gsap.timeline({ repeat: -1, repeatDelay: 6.5 });
    
    animation
      .to(mobileSmSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.1,
        ease: 'power2.in'
      })
      .to(mobileLgSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.3,
        ease: 'power2.in'
      }, '<')
      .to(mobileLgSvgRef.current, {
        scale: 1,
        rotation: 360,
        duration: 0.8,
        ease: 'back.out(1.2)'
      }, '>0.3')
      .to(mobileSmSvgRef.current, {
        scale: 1,
        duration: 0.4,
        rotation: '+=90',
        ease: 'back.out(1.7)'
      }, '<0.65');

    return () => {
      animation.kill();
    };
  }, []);

  // GSAP animation for desktop button SVG (for touch devices) - runs every 6.5 seconds
  useEffect(() => {
    if (!desktopLgSvgRef.current || !desktopSmSvgRef.current || !isTouchDevice) return;

    const animation = gsap.timeline({ repeat: -1, repeatDelay: 6.5 });
    
    animation
      .to(desktopSmSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.1,
        ease: 'power2.in'
      })
      .to(desktopLgSvgRef.current, {
        scale: 0,
        rotation: '+=90',
        duration: 0.3,
        ease: 'power2.in'
      }, '<')
      .to(desktopLgSvgRef.current, {
        scale: 1,
        rotation: 360,
        duration: 0.8,
        ease: 'back.out(1.2)'
      }, '>0.3')
      .to(desktopSmSvgRef.current, {
        scale: 1,
        duration: 0.4,
        rotation: '+=90',
        ease: 'back.out(1.7)'
      }, '<0.65');

    return () => {
      animation.kill();
    };
  }, [isTouchDevice]);

  return (
    services.length > 0 && (
      <>
        {/* Spacer before - only on mobile */}
        {/* <div className="w-full min-h-[50vh] md:hidden" /> */}
        
        {/* <section ref={sectionRef} className="services-section overflow-hidden pt-32 pb-10 md:py-0"> */}
        <section ref={sectionRef} className="services-section overflow-hidden">
          {/* Heading */}
          {/* Also used as trigger for services section */}

          {/* <div className="max-w-8xl mx-auto global-padding md:global-padding-0"> */}
          <div className="global-padding pb-6">
            <Heading 
              title="What we do"
              body=""
              showHr={false}
              disableAnimation={true}
            />
          </div>

          {/* <div className="max-w-8xl mx-auto md:global-padding-0 mt-[26px] md:mt-[50px] "> */}
          <div className="global-padding">
            {/* Desktop: Horizontal layout, Mobile: Vertical with card wrappers */}
            <div ref={cardsContainerRef} className="relative">
              {/* Desktop Layout - flex row */}
              <div className="desktop-cards hidden md:flex md:flex-row md:gap-0" role="list" aria-label="What we do services">
                {services.map((service: Service | ManualService, idx: number) => {
                  const heading = service.heading;
                  const isReactElement = typeof heading !== 'string';
                  const headingId = `service-heading-${idx}`;
                  
                  return (
                    <article
                      key={idx}
                      className="card-wrapper md:flex-shrink-0 md:w-[39.5rem]"
                      role="listitem"
                      aria-labelledby={headingId}
                    >
                      <div className="service-card-inner">
                        <div className="bg-[#FDFEF6] -ml-[40px] rounded-t-3xl rounded-bl-3xl border border-gray-900 overflow-hidden h-full">
                          <div className="relative h-[80vh] max-h-[39rem]">
                        
                            
                            {/* Eyebrow */}
                            <div className="relative flex flex-col py-[1.875rem] px-[1.875rem] md:px-[2.5rem] md:py-[3.125rem]">
                              <p className="eyebrow-lg havas-red mb-2">
                                {service.title}
                              </p>

                              {/* Heading */}
                              <div className="mt-auto">
                                {isReactElement ? (
                                  <h2 id={headingId} className="heading-3">
                                    {heading}
                                  </h2>
                                ) : (
                                  <h2 id={headingId} className="heading-3">
                                    {heading as string}
                                  </h2>
                                )}
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
                
                {/* Desktop - Special wider card */}
                <div className="card-services-ai card-wrapper md:flex-shrink-0 md:w-screen cursor-pointer md:mb-8">
                  <div 
                    className="service-card-inner"
                    onClick={() => setIsModalOpen(true)}
                    onMouseMove={handleAICardMouseMove}
                    onMouseEnter={handleAICardMouseEnter}
                    onMouseLeave={handleAICardMouseLeave}
                  >
                    <div className="-ml-[40px] rounded-t-3xl rounded-bl-3xl border border-gray-900 overflow-hidden transition-all duration-300 h-full">
                      <div className="relative h-[80vh] max-h-[39rem] w-full">
                        <ShaderBackground />
                        <div className="relative h-full flex flex-col p-10 pt-20 z-10">
                          <h3 className="heading-3 text-gray-800 md:w-[43%]">What are you solving for?</h3>
                          {/* Show button for touch devices (tablets) */}
                          {isTouchDevice && (
                            <div className="mt-auto">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  trackEvent('hp_click', {
                                    click_text: 'Start chat',
                                    page_path: pathname
                                  });
                                  setIsModalOpen(true);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    trackEvent('hp_click', {
                                      click_text: 'Start chat',
                                      page_path: pathname
                                    });
                                    setIsModalOpen(true);
                                  }
                                }}
                                aria-label="Start chat"
                                className="launch-ai-button inline-flex items-center justify-center rounded-full text-black font-medium border border-black focus:ring-2 focus:ring-blue-500 focus:outline-none py-[1.06rem] px-[1.85rem] bg-[#AAFA96] w-auto max-w-[25rem]"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="text-black relative flex-shrink-0 w-[1.5rem] h-[1.5rem] flex items-center justify-center overflow-visible">
                                    <div ref={desktopLgSvgRef} className="absolute inset-0 flex items-center justify-center">
                                      <AiShapeLg />
                                    </div>
                                    <div ref={desktopSmSvgRef} className="absolute flex items-center justify-center" style={{ top: 'calc(50% - 1.0625rem)', left: 'calc(50% + 0.375rem)' }}>
                                      <AiShapeSm />
                                    </div>
                                  </div>
                                  <span className="body-sm">
                                    Start chat
                                  </span>
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Layout - stacking cards */}
              <div className="mobile-cards md:hidden w-full" role="list" aria-label="What we do services">
                {services.map((service: Service | ManualService, idx: number) => {
                  const heading = service.heading;
                  const isReactElement = typeof heading !== 'string';
                  const headingId = `service-heading-mobile-${idx}`;
                  
                  return (
                    <article
                      key={idx}
                      className="card-wrapper w-full last:mb-12"
                      style={{ perspective: '25rem' }}
                      role="listitem"
                      aria-labelledby={headingId}
                    >
                      <div className="service-card-inner w-full">
                        <div className="bg-[#FDFEF6] rounded-3xl border border-gray-900 overflow-hidden">
                          <div className="relative h-[25rem] max-h-[39rem]">
                            
                            {/* Content */}
                            <div className="relative flex flex-col p-8">
                              <p className="eyebrow-lg havas-red">
                                {service.title}
                              </p>

                              {/* Description at bottom */}
                              <div className="mt-auto space-y-4">
                                {isReactElement ? (
                                  <h2 id={headingId} className="heading-3">
                                    {heading}
                                  </h2>
                                ) : (
                                  <h2 id={headingId} className="heading-3">
                                    {heading as string}
                                  </h2>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
                
                {/* Mobile - Special card */}
                <div
                  className="card-wrapper w-full mb-24"
                  style={{ perspective: '31.25rem' }}
                >
                  <div 
                    className="service-card-inner w-full cursor-pointer"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <div 
                      className="rounded-3xl border border-gray-900 overflow-hidden transition-all duration-300"
                      style={{
                        background: 'radial-gradient(54.14% 76.28% at 78.06% 36.78%, #8DC1FF 0%, #9BDECA 50.4%, #AAFA96 100%)'
                      }}
                    >
                      <div className="relative h-[31.25rem] max-h-[39rem] w-full">
                        <div className="relative h-full flex flex-col p-8 z-10">
                          <h3 className="heading-3 text-gray-800">What are you solving for?</h3>
                          <div className="mt-auto">
                            <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              trackEvent('hp_click', {
                                click_text: 'Start chat',
                                page_path: pathname
                              });
                              setIsModalOpen(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                trackEvent('hp_click', {
                                  click_text: 'Start chat',
                                  page_path: pathname
                                });
                                setIsModalOpen(true);
                              }
                            }}
                            aria-label="Start chat"
                            className="launch-ai-button inline-flex items-center justify-center rounded-full text-black font-medium border border-black focus:ring-2 focus:ring-blue-500 focus:outline-none py-[1.06rem] px-[1.85rem] bg-[#AAFA96] w-full md:w-auto md:max-w-[25rem]"
                          >
                            <div className="flex items-center gap-2">
                              <div className="text-black relative flex-shrink-0 w-[1.5rem] h-[1.5rem] flex items-center justify-center overflow-visible">
                                <div ref={mobileLgSvgRef} className="absolute inset-0 flex items-center justify-center">
                                  <AiShapeLg />
                                </div>
                                <div ref={mobileSmSvgRef} className="absolute flex items-center justify-center" style={{ top: 'calc(50% - 1.0625rem)', left: 'calc(50% + 0.375rem)' }}>
                                  <AiShapeSm />
                                </div>
                              </div>
                              <span className="body-sm">
                                Start chat
                              </span>
                            </div>
                          </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* <div className="text-center mt-[30px]"> */}
          {/* <div className="text-center -mt-12 md:mt-0"> */}
          <div className="text-center -mt-8 md:mt-0">
            <CTAButton 
              href="/what-we-do"
              onClick={() => {
                trackEvent('hp_click', {
                  click_text: 'More about what we do',
                  click_url: '/what-we-do',
                  page_path: pathname
                });
              }}
            >
              More about what we do
            </CTAButton>
          </div>
        </section>
        
        {/* Spacer after - only on mobile */}
        {/* <div className="w-full min-h-[50vh] md:hidden" /> */}

        {/* Floating Start chat - follows mouse (Desktop only, non-touch devices) */}
        {!isTouchDevice && isAICardHovered && buttonPosition && (
          <div
            className="hidden md:block fixed pointer-events-none z-50"
            style={{
              left: `${buttonPosition.x}px`,
              top: `${buttonPosition.y}px`,
              transform: 'translate(-50%, -50%)',
              willChange: 'transform',
            }}
          >
            <style jsx>{`
              @keyframes fadeInUp {
                from {
                  transform: translateY(3.125rem) scale(0);
                }
                to {
                  transform: translateY(0) scale(1);
                }
              }
              .chat-ava-float {
                animation: fadeInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
              }
            `}</style>
            <div className="chat-ava-float inline-flex items-center gap-3 rounded-full text-black font-medium border border-black py-[1.06rem] px-[1.85rem] bg-[#AAFA96] shadow-lg whitespace-nowrap flex-shrink-0">
              <div className="text-black relative flex-shrink-0 w-[1.5rem] h-[1.5rem] flex items-center justify-center overflow-visible">
                <div ref={lgSvgRef} className="absolute inset-0 flex items-center justify-center">
                  <AiShapeLg />
                </div>
                <div ref={smSvgRef} className="absolute flex items-center justify-center" style={{ top: 'calc(50% - 1.0625rem)', left: 'calc(50% + 0.375rem)' }}>
                  <AiShapeSm />
                </div>
              </div>
              <span className="body-sm whitespace-nowrap">
              Start chat
              </span>
            </div>
          </div>
        )}
        
        {/* Custom Tooltip - Hidden on mobile */}
        {false && showTooltip && (
          <div
            className="fixed pointer-events-none z-50 transition-opacity duration-200 hidden md:block"
            style={{
              left: `${tooltipPosition.x - 200}px`,
              top: `${tooltipPosition.y + 20}px`,
            }}
          >
            <div 
              className="bg-black text-white px-4 py-3 flex items-center gap-3 shadow-xl"
              style={{
                borderRadius: '0.75rem 0 0.75rem 0.75rem',
              }}
            >
              {/* Icon */}
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path 
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              {/* Text */}
              <span className="text-sm font-medium whitespace-nowrap">
              Enter response to Start chat
              </span>
            </div>
          </div>
        )}
        
        <ContactModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
        />
      </>
    )
  );
}